import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Undo2, AlertTriangle, Clock, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { Card, Loading } from "../components/UI";
import { naira, formatDate, formatDateTime, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";

export default function LeaseRecycleBin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/lease-recycle-bin"); setItems(data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    if (!confirm("Restore this lease back to the main list?")) return;
    setRestoring(id);
    try {
      const { data } = await api.post(`/leases/${id}/restore`);
      toast.success(`Restored to "${data.restored_status}" status`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRestoring(null); }
  };

  if (loading) return <Loading/>;

  return (
    <div className="space-y-6">
      <div className="fade-up flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24] flex items-center gap-2">
            <Trash2 size={26} className="text-[#C92A2A]"/> Lease Recycle Bin
          </h1>
          <p className="text-[#5E7166] text-sm">Soft-deleted leases from the last 30 days. After 30 days they are purged permanently.</p>
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="w-14 h-14 rounded-full bg-[#E8F5EE] text-[#0E5C3A] mx-auto mb-3 flex items-center justify-center"><Trash2 size={22}/></div>
            <h3 className="font-serif text-xl font-bold">Bin is empty</h3>
            <p className="text-[#5E7166] mt-2">No deleted leases in the last 30 days. <Link to="/leases" className="text-[#0E5C3A] underline">Back to Leases</Link>.</p>
          </div>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="modav-table" data-testid="recycle-bin-table">
              <thead><tr>
                <th>Tenant / Unit</th>
                <th>Status when deleted</th>
                <th>Period</th>
                <th>Rent</th>
                <th>Deleted</th>
                <th>Reason</th>
                <th></th>
              </tr></thead>
              <tbody>
                {items.map(l => {
                  const daysAgo = l.deleted_at
                    ? Math.max(0, Math.round((new Date() - new Date(l.deleted_at)) / 86400000))
                    : "?";
                  const daysLeft = Math.max(0, 30 - daysAgo);
                  return (
                    <tr key={l.id} data-testid={`bin-row-${l.id}`}>
                      <td>
                        <div className="font-semibold">{l.tenant_name || "—"}</div>
                        <div className="text-xs text-[#5E7166]">{l.unit_name || "—"}</div>
                      </td>
                      <td>
                        <span className={statusBadgeClass(l.deleted_from_status)}>{statusLabel(l.deleted_from_status)}</span>
                        {l.deleted_force && (
                          <span className="ml-1 inline-flex items-center gap-1 text-[10px] bg-[#FEF0F0] text-[#C92A2A] font-bold px-1.5 py-0.5 rounded" title="Forced delete on a signed lease — strict warning was confirmed by admin">
                            <ShieldAlert size={10}/> FORCED
                          </span>
                        )}
                      </td>
                      <td className="text-xs">{formatDate(l.lease_start_date)}<br/>→ {formatDate(l.lease_end_date)}</td>
                      <td className="font-serif font-bold text-[#0E5C3A]">{naira(l.rent_amount)}</td>
                      <td className="text-xs">
                        <div>{formatDateTime(l.deleted_at)}</div>
                        <div className="text-[#5E7166]">by {l.deleted_by_name || "—"}</div>
                        <div className={daysLeft <= 7 ? "text-[#C92A2A] font-semibold" : "text-[#5E7166]"}>
                          <Clock size={10} className="inline mr-1"/> {daysLeft}d left in bin
                        </div>
                      </td>
                      <td className="text-xs max-w-xs" title={l.deleted_reason || ""}>
                        <div className="line-clamp-2 italic text-[#5E7166]">{l.deleted_reason || "—"}</div>
                      </td>
                      <td>
                        <button
                          onClick={() => restore(l.id)}
                          disabled={restoring === l.id}
                          className="btn-gold text-xs inline-flex items-center gap-1"
                          data-testid={`restore-${l.id}`}
                        >
                          <Undo2 size={12}/> {restoring === l.id ? "Restoring…" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex items-start gap-2 text-xs text-[#5E7166] bg-[#FDF6E3]/60 p-3 rounded max-w-3xl">
        <AlertTriangle size={14} className="text-[#A0801C] mt-0.5 shrink-0"/>
        <span>
          Restored leases come back with their original status. If the unit has a new active lease in the meantime, the restore will fail to prevent double-booking — cancel the conflicting one first. Audit log keeps a permanent record of every delete and restore.
        </span>
      </div>
    </div>
  );
}
